import type { FC, ReactNode } from 'react'
import type { CurrencyCode, PaymentMethod } from '@/types'

import { PAYMENT_METHODS } from '@/constants/carwash'
import { formatMoney } from '@/lib/carwash/format'

/* Fixed colour per method, so a method keeps its colour whatever the amounts are. */
const COLORS: Record<PaymentMethod, string> = {
    upi: 'var(--chart-1)',
    cash: 'var(--chart-2)',
    card: 'var(--chart-3)',
    other: 'var(--chart-4)'
}

const ORDER: PaymentMethod[] = ['upi', 'cash', 'card', 'other']

interface MethodSplitProps {
    amounts: Record<string, number>
    currency: CurrencyCode
}

/* How collections split across UPI, cash, card and other, as one stacked bar with a labelled legend. */
export const MethodSplit: FC<MethodSplitProps> = ({ amounts, currency }): ReactNode => {
    const total = ORDER.reduce((sum, method) => sum + (amounts[method] ?? 0), 0)
    const label = PAYMENT_METHODS.reduce<Record<string, string>>((map, method) => ({ ...map, [method.value]: method.label }), {})

    const summary = total
        ? ORDER.map(method => `${label[method]} ${formatMoney(amounts[method] ?? 0, currency)}`).join(', ')
        : 'No payments yet'

    return (
        <div>
            <div className='flex h-3 gap-0.5 overflow-hidden rounded-full bg-slate-100' role='img' aria-label={`Collections by payment method: ${summary}`}>
                {total > 0 && ORDER.map(method => {
                    const value = amounts[method] ?? 0
                    if (!value) return null

                    return (
                        <span
                            key={method}
                            className='h-full first:rounded-l-full last:rounded-r-full'
                            style={{ width: `${(value / total) * 100}%`, backgroundColor: COLORS[method] }}
                        />
                    )
                })}
            </div>

            <dl className='mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4'>
                {ORDER.map(method => {
                    const value = amounts[method] ?? 0

                    return (
                        <div key={method} className='flex items-center gap-2'>
                            <span className='size-2.5 shrink-0 rounded-sm' style={{ backgroundColor: COLORS[method] }} aria-hidden='true' />

                            <dt className='text-[13px] text-muted-foreground'>
                                {label[method]}
                            </dt>

                            <dd className='ml-auto text-[13px] font-semibold text-foreground tabular-nums sm:ml-1'>
                                {formatMoney(value, currency)}
                                {total > 0 && (
                                    <span className='ml-1 font-normal text-muted-foreground'>
                                        {`(${Math.round((value / total) * 100)}%)`}
                                    </span>
                                )}
                            </dd>
                        </div>
                    )
                })}
            </dl>
        </div>
    )
}
